import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isSupabaseConfigured } from '../config/supabase';
import './Login.css';

const LOGIN_AS = { ADMIN: 'admin', VENDR: 'vendr' };
const LOGIN_TIMEOUT_MS = 18000;
const ROLE_WAIT_MS = 5000; // If role still null after this, show refresh prompt instead of spinning

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginAs, setLoginAs] = useState(LOGIN_AS.VENDR); // 'admin' | 'vendr'
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [roleWaitExpired, setRoleWaitExpired] = useState(false);
  const roleWaitRef = useRef(null);
  const refetchRoleCalledRef = useRef(false);
  const { login, logout, isAuthenticated, role, loading: authLoading, refetchRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const successMessage = location.state?.message;

  // Redirect authenticated users away from login (a: shouldn't see login when logged in)
  useEffect(() => {
    if (authLoading) return;
    if (isAuthenticated && (role === 'admin' || role === 'stakeholder')) {
      navigate('/', { replace: true });
      return;
    }
    if (isAuthenticated && role === 'client') {
      navigate('/vendr', { replace: true });
      return;
    }
    // If authenticated but role is null, refetch role once and start timeout (avoid multiple refetchRole calls)
    if (isAuthenticated && role == null) {
      setRoleWaitExpired(false);
      if (!refetchRoleCalledRef.current) {
        refetchRoleCalledRef.current = true;
        refetchRole?.();
      }
      roleWaitRef.current = setTimeout(() => setRoleWaitExpired(true), ROLE_WAIT_MS);
    } else {
      refetchRoleCalledRef.current = false;
      if (roleWaitRef.current) clearTimeout(roleWaitRef.current);
      roleWaitRef.current = null;
    }
    return () => {
      if (roleWaitRef.current) clearTimeout(roleWaitRef.current);
    };
  }, [authLoading, isAuthenticated, role, navigate, refetchRole]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!isSupabaseConfigured) {
      setError(
        'Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to .env (in marketvendr-dashboard) and restart the dev server.'
      );
      return;
    }
    setLoading(true);

    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('LOGIN_TIMEOUT')), LOGIN_TIMEOUT_MS)
    );
    let result;
    try {
      result = await Promise.race([login(email, password), timeoutPromise]);
    } catch (err) {
      setLoading(false);
      if (err?.message === 'LOGIN_TIMEOUT') {
        setError(
          'Login is taking too long. If you just signed in, try refreshing the page — you may already be logged in. Otherwise check your connection and .env (REACT_APP_SUPABASE_URL, REACT_APP_SUPABASE_ANON_KEY).'
        );
        return;
      }
      setError(err?.message || 'Login failed. Please try again.');
      return;
    }
    setLoading(false);

    if (!result.success) {
      setError(result.error || 'Login failed. Please try again.');
      return;
    }

    const role = result.role;
    if (!role) {
      setError(
        "We couldn't load your account role. If you have access, try refreshing the page or logging in again in a moment."
      );
      return;
    }

    // Validate toggle matches account role (Admin toggle = admin or stakeholder)
    if (loginAs === LOGIN_AS.ADMIN && role !== 'admin' && role !== 'stakeholder') {
      setError('This account is a Vendr account. Switch to "Vendr" to log in.');
      return;
    }
    if (loginAs === LOGIN_AS.VENDR && role !== 'client') {
      setError('This account is an Admin or Stakeholder account. Switch to "Admin" to log in.');
      return;
    }

    // Only admins are sent to change-password when flagged. Clients set their own password at registration.
    if (role === 'admin' && result.user?.user_metadata?.must_change_password) {
      navigate('/change-password', {
        state: { from: '/' },
        replace: true,
      });
      return;
    }

    if (role === 'admin' || role === 'stakeholder') {
      navigate('/');
    } else {
      navigate('/vendr');
    }
  };

  // Show loading while checking auth or while authenticated (so we can redirect once role is known)
  if (authLoading || isAuthenticated) {
        const stuck = isAuthenticated && role == null && roleWaitExpired;
    return (
      <div className="login-page" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', gap: '1rem' }}>
        {stuck ? (
          <>
            <p>We couldn&apos;t load your account role. You can try again, refresh, or log out and sign in again.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', width: '100%', maxWidth: '280px' }}>
              <button
                type="button"
                className="login-button"
                onClick={async () => {
                  setRoleWaitExpired(false);
                  await refetchRole?.();
                  roleWaitRef.current = setTimeout(() => setRoleWaitExpired(true), ROLE_WAIT_MS);
                }}
              >
                Try again
              </button>
              <button type="button" className="login-button" onClick={() => window.location.reload()}>
                Refresh page
              </button>
              <button
                type="button"
                className="login-button"
                style={{ background: '#555' }}
                onClick={async () => {
                  await logout?.();
                  setRoleWaitExpired(false);
                }}
              >
                Log out
              </button>
            </div>
          </>
        ) : (
          <p>{isAuthenticated ? 'Taking you to the dashboard...' : 'Loading...'}</p>
        )}
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h1>MarketVendr</h1>
          <h2>Dashboard</h2>
        </div>

        <div className="login-toggle-group">
          <span className="login-toggle-label">Log in as</span>
          <div className="login-toggle">
            <button
              type="button"
              className={`login-toggle-btn ${loginAs === LOGIN_AS.ADMIN ? 'active' : ''}`}
              onClick={() => setLoginAs(LOGIN_AS.ADMIN)}
              aria-pressed={loginAs === LOGIN_AS.ADMIN}
            >
              Admin
            </button>
            <button
              type="button"
              className={`login-toggle-btn ${loginAs === LOGIN_AS.VENDR ? 'active' : ''}`}
              onClick={() => setLoginAs(LOGIN_AS.VENDR)}
              aria-pressed={loginAs === LOGIN_AS.VENDR}
            >
              Vendr
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {successMessage && <div className="success-message">{successMessage}</div>}
          {error && <div className="error-message">{error}</div>}
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <button type="submit" className="login-button" disabled={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;

